import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, List, ListItem, ListItemButton, ListItemText, Tooltip, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward, Edit } from '@mui/icons-material';
import { HelpPageCategory } from '../models/helpPageCategory';

type HelpPageCategoryTreeProps = {
  categories: HelpPageCategory[];
  onEdit: (category: HelpPageCategory) => void;
  onMove: (category: HelpPageCategory, direction: -1 | 1) => void;
};

const bySortOrder = (a: HelpPageCategory, b: HelpPageCategory) => a.sortOrder - b.sortOrder;

export const HelpPageCategoryTree = (props: HelpPageCategoryTreeProps) => {
  const { categories, onEdit, onMove } = props;
  const { t } = useTranslation(['helpArticles']);

  const mainCategories = categories.filter((c) => c.parentId === null).sort(bySortOrder);

  if (mainCategories.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
        {t('helpArticles:categories.empty')}
      </Typography>
    );
  }

  const renderRow = (category: HelpPageCategory, siblings: HelpPageCategory[], isSubCategory: boolean) => {
    const index = siblings.findIndex((c) => c.id === category.id);

    return (
      <ListItem
        key={category.id}
        disablePadding
        secondaryAction={
          <Box>
            <Tooltip title={t('helpArticles:categories.moveUp')}>
              <span>
                <IconButton size='small' disabled={index === 0} onClick={() => onMove(category, -1)}>
                  <ArrowUpward fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('helpArticles:categories.moveDown')}>
              <span>
                <IconButton size='small' disabled={index === siblings.length - 1} onClick={() => onMove(category, 1)}>
                  <ArrowDownward fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('helpArticles:categories.edit')}>
              <IconButton size='small' onClick={() => onEdit(category)}>
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        }
      >
        <ListItemButton sx={{ pl: isSubCategory ? 5 : 2 }} onClick={() => onEdit(category)}>
          <ListItemText primary={category.name} primaryTypographyProps={{ variant: 'body2', fontWeight: isSubCategory ? 400 : 600 }} />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <List dense disablePadding>
      {mainCategories.map((mainCategory) => {
        const subCategories = categories.filter((c) => c.parentId === mainCategory.id).sort(bySortOrder);

        return (
          <Fragment key={mainCategory.id}>
            {renderRow(mainCategory, mainCategories, false)}
            {subCategories.map((subCategory) => renderRow(subCategory, subCategories, true))}
            <Divider />
          </Fragment>
        );
      })}
    </List>
  );
};
